// The country pages a page links to by name, in the page's own language.
//
// The hub at /passport-ocr lists every indexed country page, but it is English
// only, so a translated country page such as /de/germany-passport-ocr was
// reachable from no page of its own language. These links close that gap: the
// footer and the pages that talk about the zones (the home page and the MRZ
// formats guide) name the country pages the reader's language carries.
//
// The country table itself ($lib/seo/countries) is not imported: it holds two
// hundred rows, and this module ends up in the script of every page the footer
// is on. The few codes needed here are copied instead, and the unit suite
// beside this file checks each one against its row of the table.

import { DEFAULT_LOCALE, LOCALE_INFO, type Locale } from "$lib/i18n/locales";
import { countriesFor, countryPagePath, localizeHref } from "$lib/i18n/registry";
import { isIndexedCountry } from "./country-index";

/**
 * The ISO 3166-1 alpha-2 code of every country page linked from here, which
 * is what the page's language names the country by.
 */
export const LINKED_COUNTRY_CODES: Readonly<Record<string, string>> = {
  "united-states": "US",
  "united-kingdom": "GB",
  germany: "DE",
  france: "FR",
  spain: "ES",
  mexico: "MX",
  brazil: "BR",
  indonesia: "ID",
  vietnam: "VN",
  japan: "JP",
  "south-korea": "KR",
  china: "CN",
  turkey: "TR",
  "united-arab-emirates": "AE",
  "saudi-arabia": "SA",
  italy: "IT",
  india: "IN",
};

/**
 * The English pages' selection: the markets with the most searches for a
 * country's passport among the indexed pages. The hub carries the rest.
 */
export const ENGLISH_LINKED_COUNTRIES: readonly string[] = [
  "united-states",
  "united-kingdom",
  "india",
  "germany",
  "france",
  "japan",
];

/** One link to a country page. */
export interface CountryLink {
  readonly slug: string;
  /** The address in the page's language. */
  readonly href: string;
  /** The country's name in the page's language. */
  readonly name: string;
}

/** The country's name in the page's language, or the slug's English words when the runtime has none. */
function nameOf(slug: string, locale: Locale): string {
  const code = LINKED_COUNTRY_CODES[slug];
  const fallback = slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  if (code === undefined) return fallback;
  try {
    const name = new Intl.DisplayNames([LOCALE_INFO[locale].lang], { type: "region" }).of(code);
    return name === undefined || name === code ? fallback : name;
  } catch {
    return fallback;
  }
}

/**
 * The country pages a page in `locale` links to. A translated page links to
 * the ones its language carries; only indexed pages are linked, because a link
 * to a page that asks to be dropped from the index asks for a crawl and
 * refuses its result ($lib/seo/country-index).
 */
export function countryLinks(locale: Locale): readonly CountryLink[] {
  const slugs = locale === DEFAULT_LOCALE ? ENGLISH_LINKED_COUNTRIES : countriesFor(locale);
  return slugs.filter(isIndexedCountry).map((slug) => ({
    slug,
    href: localizeHref(countryPagePath(slug), locale),
    name: nameOf(slug, locale),
  }));
}

/** The link text: the language's pattern with the country's name in place of `{country}`. */
export function countryLinkText(pattern: string, name: string): string {
  return pattern.replace("{country}", name);
}
